import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { ContentSkeleton } from "@/components/dashboard/premium/ContentSkeleton";
import { AccountStatusGate } from "@/components/account/AccountStatusGate";

interface AuthRouteProps {
  children: React.ReactNode;
}

/**
 * Lightweight auth gate. Designed to render INSIDE the persistent
 * AppShellLayout, so we never show a full-screen spinner — the shell stays
 * mounted around it. While auth resolves we show only a content-area skeleton.
 */
export function AuthRoute({ children }: AuthRouteProps) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Small grace period to avoid races with Supabase auth state hydration
    const timeoutId = setTimeout(() => {
      if (!loading && !user) {
        navigate("/auth");
      }
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [user, loading, navigate]);

  if (loading) {
    return <ContentSkeleton />;
  }

  if (!user) {
    return null;
  }

  return <AccountStatusGate>{children}</AccountStatusGate>;
}
